<template>
  <div class="failure-report-view">
    <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-1">
      <div class="mb-1 mb-lg-0">
        <h3 class="font-weight-bolder text-primary mb-25 d-flex align-items-center">
          <feather-icon
            icon="AlertTriangleIcon"
            size="24"
            class="mr-50"
          />
          <span>{{ $t('Failures') }}</span>
        </h3>
        <p class="text-muted font-small-3 mb-0">
          {{ $t('Failure Details') }}
        </p>
      </div>

      <div
        class="d-flex align-items-center flex-wrap mt-1 mt-lg-0"
        style="gap: 8px;"
      >
        <b-button
          :variant="showFilters ? 'primary' : 'outline-primary'"
          size="sm"
          class="d-inline-flex align-items-center"
          @click="showFilters = !showFilters"
        >
          <feather-icon
            icon="FilterIcon"
            size="14"
            class="mr-50"
          />
          <span>{{ showFilters ? $t('Hide Filters') : $t('Filters') }}</span>
          <b-badge
            v-if="activeFiltersCount > 0"
            variant="light-danger"
            pill
            class="ml-50"
          >
            {{ activeFiltersCount }}
          </b-badge>
        </b-button>

        <b-button
          v-b-tooltip.hover
          variant="outline-primary"
          size="sm"
          class="btn-icon"
          :title="$t('Update Data')"
          :disabled="isLoading"
          @click="applyFilters"
        >
          <feather-icon
            icon="RotateCwIcon"
            size="14"
            :class="{ 'spin-icon': isLoading }"
          />
        </b-button>

        <b-button
          v-b-tooltip.hover
          variant="outline-success"
          size="sm"
          class="d-inline-flex align-items-center"
          :title="$t('Export to Excel')"
          :disabled="isLoadingExcel || isLoading"
          @click="exportToExcel"
        >
          <b-spinner
            v-if="isLoadingExcel"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="DownloadIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-sm-inline">{{ $t('Export to Excel') }}</span>
        </b-button>

        <b-button
          variant="outline-primary"
          size="sm"
          class="d-inline-flex align-items-center"
          :to="{ name: 'list-maintenance-plan' }"
        >
          <feather-icon
            icon="CalendarIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-md-inline">{{ $t('Plan/Preventive') }}</span>
        </b-button>

        <b-button
          variant="outline-primary"
          size="sm"
          class="d-inline-flex align-items-center"
          :to="{ name: 'list-maintenance' }"
        >
          <feather-icon
            icon="CheckCircleIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-md-inline">{{ $t('Executed') }}</span>
        </b-button>
      </div>
    </div>

    <transition name="slide-fade">
      <b-card
        v-if="showFilters"
        no-body
        class="mb-1 shadow-sm border-0 filters-card"
      >
        <b-card-header class="d-flex justify-content-between align-items-center py-75 px-1 border-bottom">
          <div class="d-flex align-items-center">
            <feather-icon
              icon="SlidersIcon"
              size="16"
              class="text-primary mr-50"
            />
            <h6 class="mb-0 font-weight-bolder">
              {{ $t('Filters') }}
            </h6>
            <b-badge
              v-if="activeFiltersCount > 0"
              variant="light-primary"
              pill
              class="ml-50"
            >
              {{ $t('{count} active', { count: activeFiltersCount }) }}
            </b-badge>
          </div>
          <div
            class="d-flex align-items-center"
            style="gap: 6px;"
          >
            <b-button
              v-b-tooltip.hover
              variant="flat-danger"
              size="sm"
              class="btn-icon"
              :title="$t('Clear Filters')"
              @click="resetFilters"
            >
              <feather-icon
                icon="Trash2Icon"
                size="14"
              />
            </b-button>
            <b-button
              v-b-tooltip.hover
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              :title="$t('Close Filters')"
              @click="closeFilters"
            >
              <feather-icon
                icon="XIcon"
                size="16"
              />
            </b-button>
          </div>
        </b-card-header>

        <b-card-body class="p-1">
          <div
            class="mb-1 d-flex flex-wrap align-items-center"
            style="gap: 6px;"
          >
            <small class="font-weight-bold text-muted mr-50">{{ $t('Period Shortcuts:') }}</small>
            <b-badge
              v-for="preset in periodPresets"
              :key="preset.id"
              href="javascript:void(0)"
              :variant="dateRangePreset === preset.id ? 'primary' : 'light-secondary'"
              pill
              class="cursor-pointer py-50 px-75"
              @click="setDatePreset(preset.id)"
            >
              {{ preset.label }}
            </b-badge>
          </div>

          <b-row>
            <b-col
              cols="12"
              sm="6"
              lg="3"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="CalendarIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('Start Date') }}</span>
                <span class="text-danger ml-25">*</span>
              </label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                  />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="dateStart"
                  class="form-control"
                  :config="flatpickrConfig"
                  :placeholder="$t('Select...')"
                  @on-change="onCustomDateChange"
                />
              </b-input-group>
            </b-col>

            <b-col
              cols="12"
              sm="6"
              lg="3"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="CalendarIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('End Date') }}</span>
                <span class="text-danger ml-25">*</span>
              </label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                  />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="dateEnd"
                  class="form-control"
                  :config="flatpickrConfig"
                  :placeholder="$t('Select...')"
                  @on-change="onCustomDateChange"
                />
              </b-input-group>
            </b-col>

            <b-col
              cols="12"
              lg="6"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="TruckIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('Asset') }}</span>
              </label>
              <div class="d-flex">
                <vue-autosuggest
                  v-model="searchAsset"
                  class="flex-fill"
                  :suggestions="filteredOptions"
                  :input-props="inputProps"
                  :get-suggestion-value="getAssetSuggestionValue"
                  @selected="selectHandler"
                  @input="onInputChange"
                >
                  <template slot-scope="{suggestion}">
                    <span class="my-suggestion-item">{{ suggestion.item.plate ? suggestion.item.plate : suggestion.item.chassisNumber }}</span>
                  </template>
                </vue-autosuggest>
                <b-button
                  v-b-tooltip.hover
                  size="sm"
                  variant="outline-secondary"
                  class="btn-icon ml-50 flex-shrink-0"
                  :title="$t('Clear')"
                  :disabled="searchAsset.length === 0"
                  @click="clearAsset"
                >
                  <feather-icon
                    icon="XIcon"
                    size="14"
                  />
                </b-button>
              </div>
            </b-col>

            <b-col
              v-if="user.role && user.role.includes('TRCKOWN')"
              cols="12"
              md="6"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="UserIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('Customer') }}</span>
              </label>
              <div class="d-flex">
                <vue-autosuggest
                  v-model="searchCustomer"
                  class="flex-fill"
                  :suggestions="filteredOptionsCustomer"
                  :input-props="inputProps"
                  :get-suggestion-value="getCustomerSuggestionValue"
                  @selected="selectHandlerCustomer"
                  @input="onInputChangeCustomer"
                >
                  <template slot-scope="{suggestion}">
                    <span class="my-suggestion-item">{{ suggestion.item.name }}</span>
                  </template>
                </vue-autosuggest>
                <b-button
                  v-b-tooltip.hover
                  size="sm"
                  variant="outline-secondary"
                  class="btn-icon ml-50 flex-shrink-0"
                  :title="$t('Clear')"
                  :disabled="searchCustomer.length === 0"
                  @click="clearCustomer"
                >
                  <feather-icon
                    icon="XIcon"
                    size="14"
                  />
                </b-button>
              </div>
            </b-col>

            <b-col
              cols="12"
              md="6"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="AlertOctagonIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('Failure level') }}</span>
              </label>
              <multiselect
                v-model="chosenFailures"
                :options="optionFailures"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :placeholder="placeholderMultSelect"
                label="description"
                track-by="id"
                :preselect-first="false"
              >
                <template
                  slot="selection"
                  slot-scope="{ values, search, isOpen }"
                >
                  <span
                    v-if="values.length && !isOpen"
                    class="multiselect__single"
                  >{{ values.length }} {{ $t('options selected') }}</span>
                </template>
              </multiselect>
            </b-col>

            <b-col
              cols="12"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-50 text-muted">{{ $t('Legend') }}</label>
              <div
                class="d-flex flex-wrap"
                style="gap: 8px;"
              >
                <div
                  v-for="failure in optionFailures"
                  :key="failure.id"
                  class="d-flex align-items-center"
                >
                  <b-badge
                    :variant="resolveVariant(failure.id)"
                    class="legend-badge mr-50"
                  >
                    &nbsp;
                  </b-badge>
                  <span class="font-small-3">{{ failure.name }}</span>
                </div>
              </div>
            </b-col>
          </b-row>

          <div class="d-flex align-items-center justify-content-between flex-wrap mt-50 pt-75 border-top">
            <div class="text-muted font-small-2 d-flex align-items-center mb-50 mb-md-0 mr-1">
              <feather-icon
                icon="InfoIcon"
                size="14"
                class="mr-50 text-primary flex-shrink-0"
              />
              <span>{{ $t('Maximum interval of 31 days.') }}</span>
            </div>
            <div
              class="d-flex align-items-center"
              style="gap: 8px;"
            >
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="resetFilters"
              >
                {{ $t('Restore Default') }}
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                class="d-inline-flex align-items-center"
                :disabled="isLoading || !dateStart || !dateEnd"
                @click="applyFilters"
              >
                <b-spinner
                  v-if="isLoading"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SearchIcon"
                  size="14"
                  class="mr-50"
                />
                <span>{{ $t('Apply Filters') }}</span>
              </b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </transition>

    <div
      v-if="!showFilters"
      class="px-1 py-75 mb-1 border rounded bg-light-secondary d-flex flex-wrap align-items-center shadow-xs"
      style="gap: 6px;"
    >
      <small class="font-weight-bold text-muted mr-50">{{ $t('Active Filters:') }}</small>
      <b-badge
        v-if="periodLabel"
        variant="light-primary"
        pill
        class="d-inline-flex align-items-center cursor-pointer"
        @click="showFilters = true"
      >
        <feather-icon
          icon="CalendarIcon"
          size="12"
          class="mr-25"
        />
        {{ periodLabel }}
      </b-badge>
      <b-badge
        v-if="searchAsset"
        variant="light-primary"
        pill
        class="d-inline-flex align-items-center cursor-pointer"
        @click="showFilters = true"
      >
        <feather-icon
          icon="TruckIcon"
          size="12"
          class="mr-25"
        />
        {{ searchAsset }}
      </b-badge>
      <b-badge
        v-if="searchCustomer"
        variant="light-primary"
        pill
        class="d-inline-flex align-items-center cursor-pointer"
        @click="showFilters = true"
      >
        <feather-icon
          icon="UserIcon"
          size="12"
          class="mr-25"
        />
        {{ searchCustomer }}
      </b-badge>
      <b-badge
        v-if="chosenFailures.length"
        variant="light-primary"
        pill
        class="d-inline-flex align-items-center cursor-pointer"
        @click="showFilters = true"
      >
        <feather-icon
          icon="AlertOctagonIcon"
          size="12"
          class="mr-25"
        />
        {{ chosenFailures.length }} {{ $t('options selected') }}
      </b-badge>
      <b-link
        class="font-small-2 font-weight-bold ml-50 text-danger"
        @click="resetFilters"
      >
        {{ $t('Clear') }}
      </b-link>
    </div>

    <b-card
      no-body
      class="invoice-preview-card shadow-sm border-0"
      :class="transition"
    >
      <div class="table-responsive failure-report-table-wrapper">
        <table class="table table-striped table-hover failure-report-table">
          <colgroup>
            <col class="col-date">
            <col class="col-identifier">
            <col class="col-customer">
            <col class="col-failure-code">
            <col class="col-hourmeter">
            <col class="col-description">
          </colgroup>
          <thead>
            <tr class="table-header-row">
              <th
                scope="col"
                class="title-table-failure"
              >
                {{ $t('Date') }}
              </th>
              <th
                scope="col"
                class="title-table-failure"
              >
                {{ $t('Identifier') }}
              </th>
              <th
                scope="col"
                class="title-table-failure"
              >
                {{ $t('Customer') }}
              </th>
              <th
                scope="col"
                class="title-table-failure text-center"
              >
                {{ $t('Failure Code') }}
              </th>
              <th
                scope="col"
                class="title-table-failure text-center"
              >
                {{ $t('Hourmeter') }}
              </th>
              <th
                scope="col"
                class="title-table-failure text-center"
              >
                {{ $t('Description') }}
              </th>
            </tr>
          </thead>

            <tbody v-if="items.length > 0">
              <template v-for="item in items">
                <tr class="failure-day-row">
                  <td colspan="6" class="day-table">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="font-weight-bold">{{ item.date }}</span>
                      <span class="font-weight-bold">{{ item.dayWeek }}</span>
                    </div>
                  </td>
                </tr>

                <tr v-for="hour in item.hours" :key="`${item.date}-${hour.dateTime}`" class="failure-data-row">
                  <td :data-label="$t('Date')">
                    <div class="d-flex flex-column">
                      <span class="font-weight-bold text-nowrap">{{ item.date }}</span>
                      <small class="text-muted text-nowrap">às {{ formatDate(hour.dateTime) }}</small>
                    </div>
                  </td>
                  <td class="identifier-cell" :title="hour.assetDescriptor" :data-label="$t('Identifier')">
                    {{ hour.assetDescriptor }}
                  </td>
                  <td class="text-truncate" :title="hour.customerName" :data-label="$t('Customer')">{{ hour.customerName
                  }}</td>
                  <td class="text-center" :data-label="$t('Failure Code')">
                    <b-badge :variant="resolveVariant(hour.failureType)" class="badge-glow p-50">
                      <strong class="failure-code-text"> {{ hour.failureCode }}</strong>
                    </b-badge>
                  </td>
                  <td class="text-center text-nowrap hourmeter-cell" :data-label="$t('Hourmeter')">
                    <div v-if="hasHourmeter(hour)"
                      class="d-flex flex-column align-items-center align-items-sm-start justify-content-center">
                      <span class="hourmeter-value">
                        {{ formatHourmeter(hour.hourmeter) }}
                      </span>
                    </div>
                    <span v-else class="placeholder-cell">
                      {{ $t('Not found') }}
                    </span>
                  </td>
                  <td class="description-cell" :data-label="$t('Description')">
                    <p class="mb-0">
                      {{ hour.failureDescription }}
                    </p>
                  </td>
                </tr>

              </template>

            </tbody>
            <tbody v-if="items.length == 0 && !isLoading && hasSearched">
              <tr>
                <td class="text-center day-table" colspan="6">
                  <div class="py-5">
                    <feather-icon icon="SearchIcon" size="48" class="text-muted mb-1" />
                    <h5 class="text-muted">{{ $t('No matching records found') }}</h5>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-if="!hasSearched && !isLoading">
              <tr>
                <td class="text-center day-table" colspan="6">
                  <div class="py-5">
                    <feather-icon icon="FilterIcon" size="48" class="text-primary mb-2" />
                    <h4 class="text-secondary font-weight-bolder">{{ $t('Apply filters to search') }}</h4>
                    <p class="text-muted mb-0">{{ $t('Please select the desired filters above and click the search button to view the report.') }}</p>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-if="isLoading">
              <tr>
                <td class="text-center day-table" colspan="6">
                  <div>
                    <h4>{{ $t('Preparing report') }}</h4>
                    <b-spinner variant="primary" :label="$t('Report')" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Section footer table - Load More Button -->
        <div v-show="items.length > 0" class="m-3">
          <b-row>
            <b-col cols="12" class="d-flex justify-content-center">
              <b-button v-if="hasMoreData" @click="loadMore" :disabled="isLoadingMore" variant="primary"
                class="btn-load-more" size="md">
                <template v-if="isLoadingMore">
                  <b-spinner small class="mr-2" />
                  {{ $t('Loading...') }}
                </template>
                <template v-else>
                  <feather-icon icon="ChevronDownIcon" class="mr-1" />
                  {{ $t('Load More') }}
                </template>
              </b-button>
              <div v-else-if="items.length > 0" class="text-muted text-center">
                <feather-icon icon="CheckCircleIcon" size="18" class="mr-1" />
                {{ $t('All data loaded') }}
              </div>
            </b-col>
          </b-row>
        </div>
    </b-card>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  watch,
  onUnmounted,
} from '@vue/composition-api'
import store from '@/store'
import { getIntlLocale } from '@/libs/i18n/dateLocale'
import { getFlatpickrLocale } from '@/libs/i18n/flatpickrLocale'

import {
  BRow, BCol, BCard, BCardHeader, BCardBody, BTableLite, BCardText, BButton,
  BAlert, BLink, VBToggle, VBTooltip, BFormCheckbox, BModal,
  BFormInput, BFormGroup, BTh, BTr, BBadge, BFormSelect,
  BSpinner, BInputGroup, BInputGroupPrepend,
} from 'bootstrap-vue'

import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import Multiselect from 'vue-multiselect'
import { VueAutosuggest } from 'vue-autosuggest'

import assetStoreModule from '@/views/main/asset/assetStoreModule'
import queriesStoreModule from '@/views/main/queries/queriesStoreModule'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import customerStoreModule from '@/views/main/customer/customerStoreModule'

const FAILURE_LEVEL_KEYS = [
  { id: 0, nameKey: 'Failure Level 0 - Unmapped', descriptionKey: 'Failure Level 0 (Light Gray)' },
  { id: 1, nameKey: 'Failure Level 1 - Informative', descriptionKey: 'Failure Level 1 (Blue)' },
  { id: 2, nameKey: 'Failure Level 2 - Attention', descriptionKey: 'Failure Level 2 (Orange)' },
  { id: 3, nameKey: 'Failure Level 3 - Mandatory Stop', descriptionKey: 'Failure Level 3 (Red)' },
];

export default {
  directives: {
    'b-toggle': VBToggle,
    'b-tooltip': VBTooltip,
  },

  components: {
    BRow, BCol, BCard, BCardHeader, BCardBody, BTableLite, BCardText, BButton,
    BAlert, BLink, BFormCheckbox, BModal, BFormInput, BFormGroup,
    BTh, BTr, BBadge, BFormSelect, BSpinner,
    BInputGroup, BInputGroupPrepend,
    VuePerfectScrollbar, flatPickr, vSelect, Multiselect, VueAutosuggest
  },

  setup(props, context) {
    const { root } = context

    const ASSET_APP_STORE_MODULE_NAME = 'app-asset'
    const QUERIES_APP_STORE_MODULE_NAME = 'app-queries'
    const CUSTOMER_APP_STORE_MODULE_NAME = 'app-customer'

    if (!store.hasModule(QUERIES_APP_STORE_MODULE_NAME)) store.registerModule(QUERIES_APP_STORE_MODULE_NAME, queriesStoreModule)
    if (!store.hasModule(ASSET_APP_STORE_MODULE_NAME)) store.registerModule(ASSET_APP_STORE_MODULE_NAME, assetStoreModule)
    if (!store.hasModule(CUSTOMER_APP_STORE_MODULE_NAME)) store.registerModule(CUSTOMER_APP_STORE_MODULE_NAME, customerStoreModule)

    onUnmounted(() => {
      if (store.hasModule(QUERIES_APP_STORE_MODULE_NAME)) store.unregisterModule(QUERIES_APP_STORE_MODULE_NAME)
      if (store.hasModule(ASSET_APP_STORE_MODULE_NAME)) store.unregisterModule(ASSET_APP_STORE_MODULE_NAME)
      if (store.hasModule(CUSTOMER_APP_STORE_MODULE_NAME)) store.unregisterModule(CUSTOMER_APP_STORE_MODULE_NAME)
    })

    const user = localStorage.userData != undefined ? JSON.parse(localStorage.userData) : {}

    const isLoading = ref(false)
    const transition = ref('')
    const titleTootipBtnFilter = ref(root.$t('Open Filters'))
    const titleTootipBtnCloseFilter = ref(root.$t('Close Filters'))
    const titleBtnSearch = ref(root.$t('Search'))
    const titleTootipDateRange = ref(root.$t('Maximum interval of 31 days.'))

    const date = ref([])
    const dateStart = ref('')
    const dateEnd = ref('')
    const dateRangePreset = ref('last7')
    const ignoreDateChange = ref(false)
    const currentPage = ref(1)
    const hasMoreData = ref(true)
    const isLoadingMore = ref(false)
    const showFilters = ref(false)

    const flatpickrConfig = reactive({
      maxDate: new Date(),
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: 'd/m/Y',
      altInputClass: 'form-control form-control-sm',
      enableTime: false,
      locale: getFlatpickrLocale(),
    })

    const isLoadingExcel = ref(false)

    const searchAsset = ref('')
    const assetId = ref('')
    const filteredOptions = ref([])
    const inputProps = reactive({
      id: 'autosuggest__input',
      class: 'form-control',
      placeholder: root.$t('Search'),
    })
    const selected = ref(null)

    const filteredOptionsCustomer = ref([])
    const searchCustomer = ref('')
    const customerId = ref('')

    const items = ref([])
    const totalItems = ref(0)
    
    const buildFailureLevels = () => {
      return FAILURE_LEVEL_KEYS.map(level => ({
        id: level.id,
        name: root.$t(level.nameKey),
        description: root.$t(level.descriptionKey),
      }))
    }

    const optionFailures = ref(buildFailureLevels())
    const chosenFailures = ref(optionFailures.value.filter(failure => failure.id !== 0))

    const placeholderMultSelect = ref(root.$t('Select one or more options'))
    const hourmeterUnavailableTooltip = ref(root.$t('Could not get hourmeter for this failure'))
    const hasSearched = ref(false)

    const showSnackbar = (config = { title: '', icon: '', variant: 'info' }) => {
      root.$toast({
        component: ToastificationContent,
        props: {
          title: config.title,
          icon: config.icon,
          variant: config.variant,
        },
      })
    }

    const parseDateRange = () => {
      if (!date.value) return null;
      let start, end;
      if (typeof date.value === 'string') {
        if (date.value.includes(' to ')) {
          const dates = date.value.split(' to ');
          start = new Date(dates[0]);
          end = new Date(dates[1]);
        } else {
          start = new Date(date.value);
          end = new Date(date.value);
        }
      } else if (Array.isArray(date.value)) {
        if (date.value.length >= 1) {
          start = new Date(date.value[0]);
          end = new Date(date.value[date.value.length - 1]);
        } else {
          return null;
        }
      } else {
        return null;
      }
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return null;
      }
      
      return { start, end };
    }

    const isDateRangeValid = () => {
      const range = parseDateRange();
      if (!range) return false;
      
      const diffInMs = Math.abs(range.end - range.start);
      const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
      if (diffInDays > 31) {
        showSnackbar({
          title: root.$t('Max interval one month'),
          icon: 'InfoIcon',
          variant: 'warning'
        });
        return false;
      }
      return true;
    }

    const exportToExcel = async () => {
      if (!isDateRangeValid()) return;

      try {
        isLoadingExcel.value = true;
        const range = parseDateRange();
        if (!range) return;
        const dayStart = range.start;
        const dayEnd = range.end;

        const chosenFails = Array.isArray(chosenFailures.value) && chosenFailures.value.length > 0
          ? chosenFailures.value.map(failure => failure.id)
          : [];

        const response = await store.dispatch(`${QUERIES_APP_STORE_MODULE_NAME}/fetchFailureReportExcel`, {
          page: currentPage.value,
          assetId: assetId.value,
          customerId: customerId.value,
          dayStart: dayStart,
          dayEnd: dayEnd ? dayEnd : dayStart,
          failureTypeList: chosenFails
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        const formatDateForFile = (d) => d.toISOString().split('T')[0];
        link.setAttribute('download', `failures_${formatDateForFile(dayStart)}_${formatDateForFile(dayEnd)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        showSnackbar({
          title: root.$t('Data exported successfully to Excel'),
          icon: 'CheckIcon',
          variant: 'success'
        });
      } catch (e) {
        showSnackbar({
          title: root.$t('An error occurred while exporting data'),
          icon: 'AlertTriangleIcon',
          variant: 'danger'
        });
      } finally {
        isLoadingExcel.value = false;
      }
    }

    const getDayWeek = (dt) => {
      const month = dt.split('/')[1]
      const day = dt.split('/')[0]
      const year = dt.split('/')[2]
      const week = new Date(`${month}/${day}/${year}`).getDay()
      switch (week) {
        case 0: return root.$t('Sunday')
        case 1: return root.$t('Monday')
        case 2: return root.$t('Tuesday')
        case 3: return root.$t('Wednesday')
        case 4: return root.$t('Thursday')
        case 5: return root.$t('Friday')
        case 6: return root.$t('Saturday')
        default: return ''
      }
    }

    const responseToItem = (alarms) => {
      const groups = alarms.reduce((groupsObj, game) => {
        const dateObj = new Date(game.dateTime);
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        const d = `${day}/${month}/${year}`;
        if (!groupsObj[d]) {
          groupsObj[d] = [];
        }
        groupsObj[d].push(game);
        return groupsObj;
      }, {});

      return Object.keys(groups).map((d) => {
        return {
          date: d,
          dayWeek: getDayWeek(d),
          hours: groups[d]
        };
      });
    }

    const parseDateFromString = (dateString) => {
      if (!dateString) return new Date(0);
      const [day, month, year] = dateString.split('/');
      if (!day || !month || !year) return new Date(0);
      return new Date(year, month - 1, day);
    }

    const mergeItems = (existingItems, newItems) => {
      const itemsMap = new Map();

      existingItems.forEach(item => {
        itemsMap.set(item.date, {
          ...item,
          hours: [...item.hours]
        });
      });

      newItems.forEach(newItem => {
        const existingItem = itemsMap.get(newItem.date);
        if (existingItem) {
          existingItem.hours = [...existingItem.hours, ...newItem.hours].sort((a, b) => {
            const dateA = new Date(a.dateTime || a.datetime || 0);
            const dateB = new Date(b.dateTime || b.datetime || 0);
            return dateB - dateA;
          });
        } else {
          itemsMap.set(newItem.date, {
            ...newItem,
            hours: [...newItem.hours]
          });
        }
      });

      return Array.from(itemsMap.values()).sort((a, b) => {
        const dateA = parseDateFromString(a.date);
        const dateB = parseDateFromString(b.date);
        return dateB - dateA;
      });
    }

    const fetchFailureReport = async (resetData = true) => {
      if (!isDateRangeValid()) return;

      if (resetData) {
        items.value = [];
        currentPage.value = 1;
        hasMoreData.value = true;
      }

      isLoading.value = true;
      hasSearched.value = true;

      try {
        const range = parseDateRange();
        if (!range) return;
        const dayStart = range.start;
        const dayEnd = range.end;

        const chosenFails = Array.isArray(chosenFailures.value) && chosenFailures.value.length > 0
          ? chosenFailures.value.map(failure => failure.id)
          : [];

        const response = await store.dispatch(`${QUERIES_APP_STORE_MODULE_NAME}/fetchFailureReport`, {
          page: currentPage.value,
          assetId: assetId.value,
          customerId: customerId.value,
          dayStart: dayStart,
          dayEnd: dayEnd ? dayEnd : dayStart,
          failureTypeList: chosenFails
        });

        const newItems = responseToItem(response.data.items || []);

        if (!response.data.items || response.data.items.length === 0) {
          hasMoreData.value = false;
        } else {
          const itemsPerPage = 10;
          hasMoreData.value = response.data.items.length >= itemsPerPage;
        }

        if (resetData) {
          items.value = newItems;
        } else {
          items.value = mergeItems(items.value, newItems);
        }
      } catch (e) {
        if (resetData) {
          items.value = [];
        }
        showSnackbar({
          title: root.$t('Error fetching report'),
          icon: 'AlertTriangleIcon',
          variant: 'danger'
        });
      } finally {
        isLoading.value = false;
      }
    }

    const loadMore = async () => {
      if (isLoadingMore.value || !hasMoreData.value) return;
      
      isLoadingMore.value = true;
      currentPage.value += 1;

      try {
        await fetchFailureReport(false);
      } catch (e) {
        currentPage.value -= 1;
        showSnackbar({
          title: root.$t('Error loading more data'),
          icon: 'AlertTriangleIcon',
          variant: 'danger'
        });
      } finally {
        isLoadingMore.value = false;
      }
    }

    const formatDate = (d) => {
      const dateObj = new Date(d);
      const hours = String(dateObj.getHours()).padStart(2, '0');
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    }

    const resolveVariant = (typeId) => {
      switch (typeId) {
        case 0: return 'secondary'
        case 1: return 'info'
        case 2: return 'warning'
        case 3: return 'danger'
      }
      return 'info'
    }

    const hasHourmeter = (hour) => {
      if (!hour) return false;
      return typeof hour.hourmeter === 'number' && !Number.isNaN(hour.hourmeter) && hour.hourmeter > 0;
    }

    const formatHourmeter = (value) => {
      if (typeof value !== 'number') return null;
      return value.toLocaleString(getIntlLocale(), {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }

    const getAssetSuggestionValue = (suggestion) => {
      if (!suggestion || !suggestion.item) return '';
      return suggestion.item.plate ? String(suggestion.item.plate) : String(suggestion.item.chassisNumber);
    }

    const getCustomerSuggestionValue = (suggestion) => {
      if (!suggestion || !suggestion.item) return '';
      return String(suggestion.item.name);
    }

    const selectHandler = (option) => {
      selected.value = option.item
      filteredOptions.value = []
      searchAsset.value = option.item.plate ? option.item.plate : option.item.chassisNumber
      assetId.value = option.item.id
    }

    const selectHandlerCustomer = (option) => {
      filteredOptionsCustomer.value = []
      searchCustomer.value = option.item.name
      customerId.value = option.item.id
    }

    const onInputChange = (text) => {
      if (text === '' || text === undefined || text.length < 3) {
        assetId.value = ''
        return
      }
      store.dispatch(`${ASSET_APP_STORE_MODULE_NAME}/fetchAssets`, {
        q: searchAsset.value,
        page: 1,
        perPage: 10
      }).then(response => {
        filteredOptions.value = [{ data: response.data.data }]
      }).catch(() => {})
    }

    const onInputChangeCustomer = (text) => {
      if (text === '' || text === undefined || text.length < 3) {
        customerId.value = ''
        return
      }
      store.dispatch(`${CUSTOMER_APP_STORE_MODULE_NAME}/fetchCustomers`, {
        q: searchCustomer.value,
        page: 1,
        perPage: 10
      }).then(response => {
        filteredOptionsCustomer.value = [{ data: response.data.data }]
      }).catch(() => {})
    }

    const toYmd = (value) => {
      const year = value.getFullYear()
      const month = String(value.getMonth() + 1).padStart(2, '0')
      const day = String(value.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    const parseYmd = (value) => {
      const [year, month, day] = String(value || '').split('-').map(Number)
      if (!year || !month || !day) return null
      return new Date(year, month - 1, day)
    }

    const syncDateRange = () => {
      const start = parseYmd(dateStart.value)
      const end = parseYmd(dateEnd.value)
      if (!start || !end) return
      date.value = [start, end]
    }

    const setDefaultRange = () => {
      const today = new Date()
      const start = new Date(today)
      start.setDate(today.getDate() - 7)
      ignoreDateChange.value = true
      dateStart.value = toYmd(start)
      dateEnd.value = toYmd(today)
      dateRangePreset.value = 'last7'
      syncDateRange()
      setTimeout(() => {
        ignoreDateChange.value = false
      }, 300)
    }

    const setDatePreset = (preset) => {
      ignoreDateChange.value = true
      const today = new Date()
      let start = new Date(today)
      const end = new Date(today)

      if (preset === 'yesterday') {
        start.setDate(today.getDate() - 1)
        end.setDate(today.getDate() - 1)
      } else if (preset === 'last7') {
        start.setDate(today.getDate() - 7)
      } else if (preset === 'month') {
        start = new Date(today.getFullYear(), today.getMonth(), 1)
      }

      dateStart.value = toYmd(start)
      dateEnd.value = toYmd(end)
      dateRangePreset.value = preset
      syncDateRange()
      setTimeout(() => {
        ignoreDateChange.value = false
      }, 300)
    }

    const onCustomDateChange = () => {
      if (ignoreDateChange.value) return
      dateRangePreset.value = ''
      const start = parseYmd(dateStart.value)
      const end = parseYmd(dateEnd.value)
      if (!start || !end || start > end) return
      syncDateRange()
    }

    const applyFilters = () => {
      const start = parseYmd(dateStart.value)
      const end = parseYmd(dateEnd.value)
      if (!start || !end) {
        showFilters.value = true
        return
      }
      if (start > end) {
        ignoreDateChange.value = true
        dateStart.value = toYmd(end)
        dateEnd.value = toYmd(start)
        setTimeout(() => {
          ignoreDateChange.value = false
        }, 300)
      }
      syncDateRange()
      fetchFailureReport()
    }

    const defaultFailureLevels = () => optionFailures.value.filter(failure => failure.id !== 0)

    const clearAsset = () => {
      searchAsset.value = ''
      assetId.value = ''
      filteredOptions.value = []
    }

    const clearCustomer = () => {
      searchCustomer.value = ''
      customerId.value = ''
      filteredOptionsCustomer.value = []
    }

    const resetFilters = () => {
      setDefaultRange()
      clearAsset()
      clearCustomer()
      chosenFailures.value = defaultFailureLevels()
      fetchFailureReport()
    }

    const periodLabel = computed(() => {
      if (!dateStart.value || !dateEnd.value) return ''
      const formatLabel = (value) => {
        const [year, month, day] = value.split('-')
        return `${day}/${month}/${year}`
      }
      return `${formatLabel(dateStart.value)} — ${formatLabel(dateEnd.value)}`
    })

    const activeFiltersCount = computed(() => {
      let count = dateStart.value && dateEnd.value ? 1 : 0
      if (assetId.value) count += 1
      if (customerId.value) count += 1
      if (chosenFailures.value.length) count += 1
      return count
    })

    const periodPresets = computed(() => [
      { id: 'today', label: root.$t('Today') },
      { id: 'yesterday', label: root.$t('Yesterday') },
      { id: 'last7', label: root.$t('Last 7 days') },
      { id: 'month', label: root.$t('This Month') },
    ])

    setDefaultRange()

    watch(showFilters, (isOpen) => {
      if (!isOpen) return
      ignoreDateChange.value = true
      setTimeout(() => {
        ignoreDateChange.value = false
      }, 300)
    })

    const closeFilters = () => {
      showFilters.value = false
    }

    const msgDialog = (msg, icon) => {
      root.$swal({
        position: 'top-end',
        icon: icon,
        title: msg,
        showConfirmButton: false,
        timer: 1500 * 3,
        customClass: { confirmButton: 'btn btn-primary' },
        buttonsStyling: false,
      })
    }

    return {
      QUERIES_APP_STORE_MODULE_NAME,
      ASSET_APP_STORE_MODULE_NAME,
      CUSTOMER_APP_STORE_MODULE_NAME,
      user,
      isLoading,
      transition,
      titleTootipBtnFilter,
      titleTootipBtnCloseFilter,
      titleBtnSearch,
      titleTootipDateRange,
      date,
      dateStart,
      dateEnd,
      dateRangePreset,
      periodLabel,
      activeFiltersCount,
      periodPresets,
      currentPage,
      hasMoreData,
      isLoadingMore,
      showFilters,
      flatpickrConfig,
      isLoadingExcel,
      searchAsset,
      assetId,
      filteredOptions,
      inputProps,
      selected,
      filteredOptionsCustomer,
      searchCustomer,
      customerId,
      items,
      totalItems,
      optionFailures,
      chosenFailures,
      placeholderMultSelect,
      hourmeterUnavailableTooltip,
      hasSearched,
      buildFailureLevels,
      isDateRangeValid,
      showSnackbar,
      exportToExcel,
      fetchFailureReport,
      loadMore,
      mergeItems,
      parseDateFromString,
      responseToItem,
      getDayWeek,
      formatDate,
      resolveVariant,
      hasHourmeter,
      formatHourmeter,
      getAssetSuggestionValue,
      getCustomerSuggestionValue,
      selectHandler,
      selectHandlerCustomer,
      onInputChange,
      onInputChangeCustomer,
      closeFilters,
      setDatePreset,
      onCustomDateChange,
      applyFilters,
      resetFilters,
      clearAsset,
      clearCustomer,
      msgDialog,
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css" />
<style lang="scss">
@import '@core/scss/vue/libs/vue-autosuggest.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.failure-report-view {
  .spin-icon {
    animation: failure-report-spin 1s linear infinite;
  }

  .filters-card {
    border-radius: 0.5rem;
  }

  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: all 0.18s ease;
  }

  .slide-fade-enter,
  .slide-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px);
  }
}

@keyframes failure-report-spin {
  100% {
    transform: rotate(360deg);
  }
}
</style>
<style scoped lang="scss">
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';


.alarm-section {
  //padding: 1.5rem;
  border-bottom: 1px solid #ebe9f1;
}

.rise {
  transition-duration: .8s;
  transform: translate(0px, -26.5%);
}

.event-weekendmove {
  background-color: #ea5455 !important;
}

.day-table {
  background-color: #ebe9f1;
}

.title-table-failure {
  background-color: #f5f4f9;
}

.style-dark {
  background-color: #d3d6dc !important;
}

.ps-customizer-area {
  height: calc(100% - 83px)
}

.per-page-selector {
  width: 90px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .8s ease;
}

.fade-enter,
.fade-leave-to
/* .fade-leave-active in <2.1.8 */

  {
  opacity: 0;
  transition: opacity .8s ease;
}

.legend-badge {
  // Define uma largura e altura fixas para os emblemas da legenda, tornando-os quadrados.
  width: 1rem;
  height: 1rem;
  // Alinha o conteúdo verticalmente, útil para alguns navegadores.
  vertical-align: middle;
}

.failure-report-table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.failure-report-table {
  table-layout: fixed;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  th,
  td {
    vertical-align: middle;
    padding: 0.75rem;
  }
}

.failure-report-table .table-header-row th {
  font-weight: 600;
  background-color: #f5f4f9;
  border-top: 1px solid #ebe9f1;
  border-bottom: 1px solid #ebe9f1;
}

.failure-report-table .failure-day-row td {
  background-color: #f0eff6;
  border-top: 1px solid #dcd9e2;
  border-bottom: 1px solid #dcd9e2;
}

.failure-report-table .failure-data-row:nth-of-type(even) {
  background-color: rgba(115, 103, 240, 0.05);
}

.failure-report-table .failure-data-row td {
  border-bottom: 1px solid #ebe9f1;
}

.failure-report-table .failure-code-text {
  font-size: 1.1rem;
}

.failure-report-table .placeholder-cell {
  color: #6c757d;
  font-style: italic;
}

.failure-report-table .text-truncate {
  max-width: 100%;
}

.failure-report-table .col-date {
  width: 12%;
}

.failure-report-table .col-identifier {
  width: 16%;
}

.failure-report-table .col-customer {
  width: 16%;
}

.failure-report-table .col-failure-code {
  width: 12%;
}

.failure-report-table .col-hourmeter {
  width: 12%;
}

.failure-report-table .col-description {
  width: 32%;
}

.failure-report-table .description-cell {
  text-align: left;
  white-space: normal;
  word-break: break-word;
}

.failure-report-table .identifier-cell {
  white-space: normal;
  word-break: break-word;
}

.failure-report-table .hourmeter-cell {
  white-space: normal;
}

.failure-report-table .hourmeter-value {
  font-weight: 600;
  font-size: 1rem;
}

.failure-report-table .hourmeter-date {
  font-size: 0.75rem;
}

@media (max-width: 992px) {

  .failure-report-table th,
  .failure-report-table td {
    font-size: 0.85rem;
    padding: 0.65rem;
  }

  .failure-report-table .failure-code-text {
    font-size: 1rem;
  }

  .failure-report-table .col-description {
    width: 36%;
  }
}

@media (max-width: 768px) {

  .failure-report-table th,
  .failure-report-table td {
    font-size: 0.8rem;
    padding: 0.55rem;
  }

  .failure-report-table .failure-code-text {
    font-size: 0.95rem;
  }

  .failure-report-table .col-description {
    width: 40%;
  }
}

@media (max-width: 576px) {
  .failure-report-table {
    border-spacing: 0;
  }

  .failure-report-table thead {
    display: none;
  }

  .failure-report-table colgroup {
    display: none;
  }

  .failure-report-table tbody,
  .failure-report-table tr,
  .failure-report-table td {
    display: block;
    width: 100%;
  }

  .failure-report-table .failure-day-row td {
    padding: 0.75rem;
  }

  .failure-report-table .failure-data-row {
    margin-bottom: 1rem;
    border: 1px solid #ebe9f1;
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;
  }

  .failure-report-table .failure-data-row td {
    padding: 0.5rem 0;
    border: none;
  }

  .failure-report-table .failure-data-row td::before {
    content: attr(data-label);
    font-weight: 600;
    display: block;
    margin-bottom: 0.25rem;
    color: #6c757d;
  }

  .failure-report-table .failure-data-row td.hourmeter-cell,
  .failure-report-table .failure-data-row td.hourmeter-cell span {
    text-align: left;
  }

  .failure-report-table-wrapper {
    overflow-x: visible;
  }
}

.btn-load-more {
  min-width: 160px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
